import React, { useCallback, useEffect, useRef, useState } from "react";

const KpiCarousel = ({ children, label }: { children: React.ReactNode[]; label: string }) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollState = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    setCanScrollPrev(strip.scrollLeft > 4);
    setCanScrollNext(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    updateScrollState();
    strip.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScrollState) : null;
    observer?.observe(strip);
    return () => {
      strip.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      observer?.disconnect();
    };
  }, [updateScrollState, children.length]);

  const scrollByPage = (direction: 1 | -1) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollBy({ left: direction * strip.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="sud-kpi-carousel">
      <button
        type="button"
        className="sud-kpi-nav sud-kpi-nav-prev pdf-ignore"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollPrev}
        aria-label={`Show previous ${label}`}
      >
        <i className="ti ti-chevron-left" />
      </button>
      <div className="sud-kpi-strip" ref={stripRef} role="list" aria-label={label}>
        {React.Children.map(children, (child) => (
          <div className="sud-kpi-item d-flex" role="listitem">
            {child}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="sud-kpi-nav sud-kpi-nav-next pdf-ignore"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollNext}
        aria-label={`Show more ${label}`}
      >
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  );
};

export default KpiCarousel;
